import { on, select, addClass, removeClass, getHeight, isIosDevice, hasClass, setAttribute, ready, toggleClass, selectAll } from 'helpers/dom'
import throttle from 'lodash.throttle'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { setCookie } from '@convertdigital/gamma-cookie'

const selectors = {
  THROTTLE_TIME: 50,
  TOP_BAR_SELECTOR: '.js-topbar',
  PINNED_CLASS: 'is-pin',
  UNPINNED_CLASS: 'is-unpin',
  ACTIVE_CLASS: 'is-active',
  MAIN_CONTENT_SELECTOR: '.js-main-content',
  MAIN_CONTENT_NORMAL_STATE: 'is-normal',
  PAGE_HEADER_SELECTOR: '.js-page-header',
  ACCOUNT_MENU_SELECTOR: '.js-account-dropdown',
  ACCOUNT_MENU_OVERLAY: '.js-account-overlay',
  SEARCH_BUTTON_SELECTOR: '.js-search-button',
  SEARCH_BAR_SELECTOR: '.js-search-bar',
  SEARCH_BAR_INPUT_SELECTOR: '.js-search-bar-input',
  ACCOUTN_HEADER_SELECTOR: '.js-account-header',
  SHORTER_BANNER_V2_SELECTOR: '.js-shorter-banner-v2',
  logoutSelector: '[href="/account/logout"]'
}

class SiteHeader extends HTMLElement {
  constructor() {
    super()

    this.pageHeaderEl = select(selectors.PAGE_HEADER_SELECTOR)
    this.topBarEl = select(selectors.TOP_BAR_SELECTOR)
    this.accountMenuEl = select(selectors.ACCOUNT_MENU_SELECTOR)
    this.mainContentEl = select(selectors.MAIN_CONTENT_SELECTOR)
    this.accountMenuOverlay = select(selectors.ACCOUNT_MENU_OVERLAY)
    this.searchButtonEl = select(selectors.SEARCH_BUTTON_SELECTOR)
    this.searchBarEl = select(selectors.SEARCH_BAR_SELECTOR)
    this.pageHeaderHeight = getHeight(this.pageHeaderEl)
    this.topBarHeight = getHeight(this.topBarEl)
    this.accountHeader = select(selectors.ACCOUTN_HEADER_SELECTOR)
    this.logoutButton = select(selectors.logoutSelector, this)
    this.lastScrollY = 0
    this.currentScrollY = 0
    this.ticking = false
    this.headerResizeObserver = null
    this.setAttributeStyle()
    this.observeHeaderHeight()
    this.showSuccessLogin()
    on(
      'scroll',
      throttle(() => {
        this.handleScroll()
      }, selectors.THROTTLE_TIME),
      window
    )
    on(
      'resize',
      throttle(() => {
        this.setAttributeStyle()
      }, selectors.THROTTLE_TIME),
      window
    )
    ready(() => {
      this.handleReady()
      this.setAttributeStyle()
    })

    if (isIosDevice) {
      addClass('ios-device', document.body)
    }

    if (this.accountMenuEl) {
      this.accountMenuEl.addEventListener('click', () => {
        toggleClass(selectors.ACTIVE_CLASS, this.accountMenuEl)
      })
    }

    on('click', () => {
      if (hasClass(selectors.ACTIVE_CLASS, this.accountMenuEl)) {
        removeClass(selectors.ACTIVE_CLASS, this.accountMenuEl)
        toggleClass(selectors.ACTIVE_CLASS, this.accountMenuEl)
      }
    }, this.accountMenuOverlay)

    on('click', () => {
      const searchBarInputList = selectAll(selectors.SEARCH_BAR_INPUT_SELECTOR)
      toggleClass(selectors.ACTIVE_CLASS, this.searchBarEl)
      setTimeout(() => {
        if (!this.searchBarEl.classList.contains(selectors.ACTIVE_CLASS)) {
          searchBarInputList.forEach(searchBarInputItem => searchBarInputItem.focus())
          globalEmitter.emit(emitterProps.search.closeDropdown)
        }
      }, 200)
    }, this.searchButtonEl)

    this.accountHeader && on('click', (e) => {
      if (!window.location.pathname.includes('account')) {
        e.preventDefault()
        window.location.href = this.accountHeader.getAttribute('href')
      }
    }, this.accountHeader)

    this.logoutButton && on('click', (e) => {
      e.preventDefault()
      setCookie('customerAccessToken', '', -999)

      window.location.href = '/account/logout'
    }, this.logoutButton)
  }

  handleScroll() {
    this.currentScrollY = window.pageYOffset
    if (!this.ticking) {
      requestAnimationFrame(this.updateScroll.bind(this))
    }
    this.ticking = true
  }

  handleReady() {
    if (window.pageYOffset > this.pageHeaderEl) {
      addClass(selectors.PINNED_CLASS, this.pageHeaderEl)
    }
  }

  observeHeaderHeight() {
    const remeasure = throttle(() => {
      this.setAttributeStyle()
    }, selectors.THROTTLE_TIME)

    if (typeof ResizeObserver !== 'undefined' && this.pageHeaderEl) {
      this.headerResizeObserver = new ResizeObserver(remeasure)
      this.headerResizeObserver.observe(this.pageHeaderEl)
      if (this.topBarEl) {
        this.headerResizeObserver.observe(this.topBarEl)
      }
    }

    on('load', remeasure, window)
    on('postcode:updated', remeasure, window)

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(remeasure).catch(() => {})
    }

    // Two frames later layout/fonts have usually settled
    requestAnimationFrame(() => {
      requestAnimationFrame(remeasure)
    })
  }

  disconnectedCallback() {
    if (this.headerResizeObserver) {
      this.headerResizeObserver.disconnect()
      this.headerResizeObserver = null
    }
  }

  updateScroll() {
    if (this.currentScrollY <= this.pageHeaderHeight) {
      removeClass(selectors.UNPINNED_CLASS, this.pageHeaderEl)
      removeClass(selectors.PINNED_CLASS, this.pageHeaderEl)
    } else {
      this.handleUpdateScroll()
    }

    this.lastScrollY = this.currentScrollY
    this.ticking = false
  }

  handleUpdateScroll() {
    if (this.currentScrollY < this.lastScrollY - 10) {
      this.pin()
    } else if (this.currentScrollY > this.lastScrollY + 15) {
      this.unpin()
    }
  }

  pin() {
    if (hasClass(selectors.UNPINNED_CLASS, this.pageHeaderEl)) {
      removeClass(selectors.UNPINNED_CLASS, this.pageHeaderEl)
      addClass(selectors.PINNED_CLASS, this.pageHeaderEl)
    }
  }

  unpin() {
    if (hasClass(selectors.PINNED_CLASS, this.pageHeaderEl) || !hasClass(selectors.UNPINNED_CLASS, this.pageHeaderEl)) {
      removeClass(selectors.PINNED_CLASS, this.pageHeaderEl)
      addClass(selectors.UNPINNED_CLASS, this.pageHeaderEl)
    }
  }

  setAttributeStyle() {
    this.topBarHeight = getHeight(this.topBarEl) || 0
    this.pageHeaderHeight = getHeight(this.pageHeaderEl) || 0
    const isMobile = window.innerWidth < 768
    // The V2 banner renders its own breadcrumbs, so the mobile offset that clears
    // the standalone breadcrumbs section would leave a gap.
    const hasShorterBannerV2 = select(selectors.SHORTER_BANNER_V2_SELECTOR) != null
    const paddingTop = isMobile && !hasShorterBannerV2 ? this.pageHeaderHeight + 48 : this.pageHeaderHeight
    const stickyHeaderHeight = Math.max(0, this.pageHeaderHeight - this.topBarHeight)
    setAttribute('style', `--topbar-height: ${this.topBarHeight}px;`, this.pageHeaderEl)
    if (hasClass(selectors.MAIN_CONTENT_NORMAL_STATE, this.mainContentEl)) {
      setAttribute('style', `padding-top: ${paddingTop}px`, this.mainContentEl)
    }
    document.documentElement.style.setProperty('--page-header-height', `${paddingTop}px`)
    document.documentElement.style.setProperty('--page-topbar-height', `${this.topBarHeight}px`)
    document.documentElement.style.setProperty('--page-sticky-header-height', `${stickyHeaderHeight}px`)
  }

  showSuccessLogin() {
    customElements.whenDefined('notice-global').then(() => {
      if (window.location.search.includes('success')) {
        setTimeout(() => {
          globalEmitter.emit(emitterProps.notice.global, {
            type: 'success',
            content: "You've successfully signed in"
          })

          const urlParams = new URL(window.location.href)
          urlParams.search = ''
          window.history.replaceState({}, '', urlParams)
        }, 200)
      }
    })
  }
}
customElements.define('site-header', SiteHeader)
